import { useTranslation } from 'react-i18next'
import { Trophy } from 'lucide-react'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { TeamLeaderboard } from '@/components/charts/TeamLeaderboard'
import { formatCzkShort, formatNumber } from '@/lib/format'

export function TeamsPerformanceSlide() {
  const { t } = useTranslation()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const locale = useSettingsStore((s) => s.locale)
  if (!snapshot) return null

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="grid grid-cols-12 gap-4">
        <section className="col-span-8 rounded-3xl border border-border bg-surface-1/70 p-5">
          <header className="mb-4 flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-[0.18em] text-ink-subtle">
              {t('teams.leaderboard')}
            </h3>
            <span className="text-xs text-ink-subtle">
              {snapshot.teams.reduce((s, tm) => s + tm.members, 0)}{' '}
              {locale === 'cs' ? 'lidí celkem' : 'people in total'}
            </span>
          </header>
          <TeamLeaderboard teams={snapshot.teams} />
        </section>
        <section className="col-span-4 flex flex-col gap-3 rounded-3xl border border-brand-accent/30 bg-gradient-to-br from-surface-1/70 to-brand-accent/5 p-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-accent">
            <Trophy className="size-3.5" />
            {t('teams.starOfTheWeek')}
          </div>
          {snapshot.starOfTheWeek && (
            <>
              <p className="font-display text-3xl font-bold leading-tight text-ink">
                {snapshot.starOfTheWeek.name}
              </p>
              <div className="inline-flex w-fit items-center rounded-full border border-border bg-surface-2/70 px-3 py-1 text-xs text-ink-muted">
                {snapshot.starOfTheWeek.team}
              </div>
              <p className="text-sm leading-relaxed text-ink-muted">
                "{snapshot.starOfTheWeek.reason}"
              </p>
            </>
          )}
        </section>
      </div>

      {}
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        <section className="col-span-8 rounded-3xl border border-border bg-surface-1/70 p-5">
          <h3 className="mb-3 text-xs uppercase tracking-[0.18em] text-ink-subtle">
            {t('sales.pipeline')}
          </h3>
          <ul className="grid grid-cols-2 gap-2">
            {snapshot.pipeline.map((p) => (
              <li
                key={p.id}
                className="flex items-center gap-3 rounded-xl border border-border/80 bg-surface-2/60 px-3 py-2"
              >
                <span className="grid size-8 place-items-center rounded-lg bg-brand-accent/15 font-mono text-xs font-bold text-brand-accent">
                  {p.name
                    .split(' ')
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join('')}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">{p.name}</div>
                  <div className="text-xs text-ink-subtle">
                    {t('sales.deals', { count: p.dealsInPipeline })} · {p.closedThisMonth}{' '}
                    {locale === 'cs' ? 'closed' : 'closed'}
                  </div>
                </div>
                <span className="font-mono text-sm tabular-nums text-ink-muted">
                  {formatCzkShort(p.pipelineCzk, locale)}
                </span>
              </li>
            ))}
          </ul>
        </section>
        <section className="col-span-4 rounded-3xl border border-border bg-surface-1/70 p-5">
          <h3 className="mb-3 text-xs uppercase tracking-[0.18em] text-ink-subtle">
            {locale === 'cs' ? 'Statistiky napříč' : 'Aggregate stats'}
          </h3>
          <div className="space-y-3">
            <Stat
              label={t('teams.incidentsResolved')}
              value={formatNumber(
                snapshot.teams.reduce((s, t) => s + t.incidentsResolved, 0),
                locale,
              )}
            />
            <Stat label="CSAT" value={snapshot.support.csat.toFixed(2)} />
            <Stat
              label={t('sales.pipeline')}
              value={formatCzkShort(
                snapshot.pipeline.reduce((s, p) => s + p.pipelineCzk, 0),
                locale,
              )}
            />
            <Stat
              label={locale === 'cs' ? 'Closed tento měsíc' : 'Closed this month'}
              value={formatNumber(
                snapshot.pipeline.reduce((s, p) => s + p.closedThisMonth, 0),
                locale,
              )}
            />
          </div>
        </section>
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border/60 bg-surface-2/40 px-3 py-2.5">
      <span className="text-sm text-ink-muted">{label}</span>
      <span className="font-display text-lg font-semibold tabular-nums text-ink">{value}</span>
    </div>
  )
}
